import { useEffect, useState } from "react"
import { getWorkflowById } from "../services/WorkflowServices.js"

// A step's translations live on the workflow node, not on the request, and the
// dashboard's two request endpoints (`/request/approvers`, `/request-by-user-email`)
// don't return the workflow. So the widgets load the workflows their requests sit
// on once, next to the request fetch, and every later language change is a plain
// re-render off what is already in memory - no request is fetched again.
//
// Workflows are shared by both widgets and by the dashboard / spaces page, so the
// cache is module level: one call per workflow per session.
const workflowCache = new Map()
const inFlightRequests = new Map()

const loadWorkflow = (workflowId) => {
    if (workflowCache.has(workflowId)) {
        return Promise.resolve(workflowCache.get(workflowId))
    }
    if (inFlightRequests.has(workflowId)) {
        return inFlightRequests.get(workflowId)
    }
    const request = getWorkflowById(workflowId)
        .then((response) => {
            const workflow = response?.status === 200 ? response.data : null
            // Only a real answer is cached, so a failed call is retried on the next visit.
            if (workflow) {
                workflowCache.set(workflowId, workflow)
            }
            return workflow
        })
        .catch(() => null)
        .finally(() => {
            inFlightRequests.delete(workflowId)
        })
    inFlightRequests.set(workflowId, request)
    return request
}

// The workflows the given requests belong to, keyed by workflowId, for resolving a
// request's status into the viewer's language (see `getStatusDisplayLabel`). Only the
// set of workflow ids drives the fetch - the language is not a dependency.
export const useRequestWorkflows = (requests) => {
    // Annotated so the TS widgets can index the map by workflowId.
    const [workflows, setWorkflows] = useState(/** @type {Record<string, any>} */({}))
    // A string key so a re-render with an equal-but-new request array doesn't refetch.
    const workflowIdKey = Array.from(new Set(
        (Array.isArray(requests) ? requests : []).map(request => request?.workflowId).filter(Boolean)
    )).sort().join(",")

    useEffect(() => {
        const workflowIds = workflowIdKey ? workflowIdKey.split(",") : []
        if (!workflowIds.length) {
            return
        }
        let isCancelled = false
        Promise.all(workflowIds.map(loadWorkflow)).then((loaded) => {
            if (isCancelled) {
                return
            }
            const next = {}
            workflowIds.forEach((workflowId, index) => {
                if (loaded[index]) {
                    next[workflowId] = loaded[index]
                }
            })
            setWorkflows(prev => ({ ...prev, ...next }))
        })
        return () => {
            isCancelled = true
        }
    }, [workflowIdKey])

    return workflows
}

export default useRequestWorkflows
